import React from 'react';
import { Plus, Trash2, ChevronsLeftRightEllipsis, Globe, AlertTriangle } from 'lucide-react';
import { PlaybookEventConfig, PlaybookTransformItem } from '@/types/playbook';
import { EventSchema } from '@/types/schema';
import { EditorSelection } from './PlaybookEditorTab';
import { normalizeCodeName } from '@utils/schemaUtils';
// Define ValidationWarning type locally since it's no longer exported from utils
interface ValidationWarning {
  message: string;
  type: 'warning' | 'error';
  source?: string;
}
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/common/Tooltip';

interface PlaybookSectionsPanelProps {
  events: PlaybookEventConfig[];
  transforms: PlaybookTransformItem[];
  selection: EditorSelection;
  onSelectionChange: (selection: EditorSelection) => void;
  onAddEvent: (name: string, codeName: string, description?: string) => void;
  onDeleteEvent: (eventId: string) => void;
  baselineSchemaEvents: EventSchema[];
  allowUndefinedTransforms: boolean;
  connectionJSON: object | null; // Add connectionJSON prop
  isMobile?: boolean;
  isTablet?: boolean;
  isDesktopMedium?: boolean;
  validationWarnings: ValidationWarning[];
  playbookType: 'pipeline' | 'integration'; // Add playbookType prop
}

const PlaybookSectionsPanel: React.FC<PlaybookSectionsPanelProps> = ({
  events,
  transforms,
  selection,
  onSelectionChange,
  onAddEvent,
  onDeleteEvent,
  baselineSchemaEvents,
  allowUndefinedTransforms,
  connectionJSON,
  isMobile = false,
  validationWarnings,
  playbookType, // Destructure the new prop
}) => {
  const globalTransforms = transforms.filter(t => t.appliesToScope === 'global');
  const connectionWarnings = validationWarnings.filter(w => w.source === 'connection');
  const globalTransformWarnings = validationWarnings.filter(w => w.source === 'global');
  // Calculate the actual number of connection parameters
  const getConnectionParamsCount = () => {
    if (!connectionJSON || typeof connectionJSON !== 'object') return 0;
    
    // Count only the actual parameters in the params array
    const connectionData = connectionJSON as any;
    if (connectionData.params && Array.isArray(connectionData.params)) {
      return connectionData.params.length;
    }
    
    return 0;
  };

  const connectionParamsCount = getConnectionParamsCount();

  const handleAddEmptyEvent = () => {
    const newEventName = 'New Event';
    const newEventCodeName = normalizeCodeName(newEventName);
    
    onAddEvent(newEventName, newEventCodeName);
    
    // Auto-select the new event after it's added
    // We need to find the event by codeName as the ID is generated by the parent
    setTimeout(() => {
      const addedEvent = events.find(e => e.codeName === newEventCodeName);
      if (addedEvent) {
        onSelectionChange({ type: 'event', eventId: addedEvent.id });
      }
    }, 0);
  };

  const getEventTransforms = (eventId: string) => {
    const eventTransforms = transforms.filter(t => 
      t.appliesToScope === 'eventSpecific' && 
      t.eventIds?.includes(eventId)
    );
    
    return eventTransforms;
  };

  return (
    <div className="h-full flex flex-col">

      {/* Content */}
      <div className={`flex-1 overflow-y-auto ${isMobile ? 'p-3 space-y-4' : 'p-4 space-y-5'}`}>
        <div className="space-y-1">
          {/* Connection Section - Conditionally rendered */}
          {playbookType === 'integration' && (
            <button
              onClick={() => onSelectionChange({ type: 'connection' })}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                selection.type === 'connection'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 px-1 min-w-0">
                <ChevronsLeftRightEllipsis className="h-4 w-4" />
                <span className="font-medium truncate"><span className="underline">C</span>onnection</span>
                {connectionWarnings.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <AlertTriangle className="h-4 w-4 mt-1 text-yellow-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        {connectionWarnings.map((w, i) => (
                          <p key={i}>{w.message}</p>
                        ))}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <span className="text-sm bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full">
                {connectionParamsCount}
              </span>
            </button>
          )}

          {/* Global Transforms Section */}
          <button
            onClick={() => onSelectionChange({ type: 'global' })}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
              selection.type === 'global'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2 px-1 min-w-0">
              <Globe className="h-4 w-4" />
              <span className="font-medium truncate"><span className="underline">G</span>lobal Transforms</span>
              {globalTransformWarnings.length > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <AlertTriangle className="h-4 w-4 mt-1 text-yellow-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      {globalTransformWarnings.map((w, i) => (
                        <p key={i}>{w.message}</p>
                      ))}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <span className="text-sm bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full">
              {globalTransforms.length}
            </span>
          </button>
        </div>

        {/* Event Transforms Section */}
        <div>
          <div className="flex items-center justify-between mb-3 pl-4">
            <h4 className="text-sm text-gray-500 dark:text-gray-400">
              Event Transforms
            </h4>
            {allowUndefinedTransforms && ( // Conditionally render add event button
              <button
                onClick={handleAddEmptyEvent}
                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="space-y-1">
            {/* Configured Events List */}
            {events
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((event) => {
                const isSelected = selection.type === 'event' && selection.eventId === event.id;
                const eventTransforms = getEventTransforms(event.id);

                return (
                  <div key={event.id}>
                    <button
                      onClick={() => {
                        onSelectionChange({ type: 'event', eventId: event.id });
                      }}
                      className={`group w-full flex items-center justify-between p-3 pl-4 rounded-lg transition-colors ${
                        isSelected
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="font-medium truncate">{event.name}</div>
                      </div>
                      <div className="relative w-8 h-6 flex items-center justify-center">
                        <span className={`absolute text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full transition-opacity ${allowUndefinedTransforms ? 'group-hover:opacity-0' : ''}`}>
                          {eventTransforms.length}
                        </span>
                        {allowUndefinedTransforms && ( // Conditionally render trash icon
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteEvent(event.id);
                            }}
                            className="absolute p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </button>
                  </div>
                );
              })}

            {baselineSchemaEvents.length === 0 && events.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-500 text-center py-4">
                No events yet. Click + to add one.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaybookSectionsPanel;
